import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiConnectionService } from '../../common/utils/apiConnectionService';
import { requestWithFallback } from '../../common/utils/requestWithFallback';
import { ISmartCaptchaConfig } from './smart-captcha.types';
import './smart-captcha.types';

const SCRIPT_URL =
  'https://smartcaptcha.cloud.yandex.ru/captcha.js?render=onload&onload=__spSmartCaptchaOnload';
const TOKEN_TIMEOUT_MS = 120_000;

  /**
   * Yandex SmartCaptcha (видимый чекбокс) + remote feature flag GET v1/app/smart-captcha.
   * Enabled зависит от IP-счётчика на бэке — конфиг запрашивается на каждую попытку.
   * При enabled=false скрипт не грузится и UI не затрагивается.
   */
@Injectable({
  providedIn: 'root',
})
export class SmartCaptchaService {
  private configPromise: Promise<ISmartCaptchaConfig> | null = null;
  private scriptPromise: Promise<void> | null = null;
  private widgetId: number | null = null;
  private overlay: HTMLDivElement | null = null;
  private captchaHost: HTMLDivElement | null = null;
  private pending:
    | { resolve: (token: string) => void; reject: (err: Error) => void }
    | null = null;

  constructor(
    private http: HttpClient,
    private apiConnection: ApiConnectionService,
  ) {}

  getConfig(forceRefresh = false): Promise<ISmartCaptchaConfig> {
    if (forceRefresh) {
      this.configPromise = null;
    }
    if (!this.configPromise) {
      this.configPromise = requestWithFallback(
        '../../v1/app/smart-captcha',
        (fullUrl) => this.http.get<any>(fullUrl),
        this.apiConnection.apiUrlFallbackView,
      )
        .pipe(
          map((raw: any) => ({
            enabled: !!(raw && (raw.enabled || raw.Enabled)),
            clientKey: (raw && (raw.clientKey || raw.ClientKey)) || '',
          })),
          catchError(() => of({ enabled: false, clientKey: '' })),
        )
        .toPromise();
    }
    return this.configPromise;
  }

  async isEnabled(): Promise<boolean> {
    const cfg = await this.getConfig(true);
    return !!(cfg.enabled && cfg.clientKey);
  }

  /**
   * Если флаг выключен / бесплатные попытки ещё есть — null.
   * Если нужна капча — показывает видимый виджет и ждёт прохождения.
   */
  async resolveToken(): Promise<string | null> {
    const cfg = await this.getConfig(true);
    if (!cfg.enabled || !cfg.clientKey) {
      return null;
    }

    await this.ensureScript();
    this.showOverlay();
    await this.ensureWidget(cfg.clientKey);

    return new Promise<string>((resolve, reject) => {
      if (!window.smartCaptcha || this.widgetId == null) {
        this.hideOverlay();
        reject(new Error('Капча не загрузилась'));
        return;
      }

      const timer = window.setTimeout(() => {
        this.pending = null;
        this.hideOverlay();
        reject(new Error('Истекло время проверки капчи'));
      }, TOKEN_TIMEOUT_MS);

      this.pending = {
        resolve: (token) => {
          window.clearTimeout(timer);
          this.hideOverlay();
          resolve(token);
        },
        reject: (err) => {
          window.clearTimeout(timer);
          this.hideOverlay();
          reject(err);
        },
      };

      try {
        window.smartCaptcha.reset(this.widgetId);
      } catch (e) {
        this.pending = null;
        window.clearTimeout(timer);
        this.hideOverlay();
        reject(e instanceof Error ? e : new Error('Ошибка капчи'));
      }
    });
  }

  private ensureScript(): Promise<void> {
    if (window.smartCaptcha) {
      return Promise.resolve();
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      window.__spSmartCaptchaOnload = () => resolve();

      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Не удалось загрузить скрипт капчи'));
      };
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private showOverlay(): void {
    if (this.overlay) {
      this.overlay.style.display = 'flex';
      return;
    }

    this.overlay = document.createElement('div');
    this.overlay.id = 'sp-smart-captcha-overlay';
    this.overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:100000',
      'display:flex',
      'align-items:flex-start',
      'justify-content:center',
      'background:rgba(0,0,0,0.45)',
      'padding:24px 16px',
      'box-sizing:border-box',
    ].join(';');

    const card = document.createElement('div');
    card.style.cssText = [
      'background:#fff',
      'border-radius:12px',
      'padding:20px',
      'max-width:360px',
      'width:100%',
      'box-sizing:border-box',
      'font-family:sans-serif',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = 'Подтвердите, что вы не робот';
    title.style.cssText =
      'margin:0 0 16px;font-size:16px;font-weight:600;color:#222;text-align:center;';

    this.captchaHost = document.createElement('div');
    this.captchaHost.id = 'sp-smart-captcha-root';
    this.captchaHost.style.cssText =
      'display:flex;justify-content:center;min-height:100px;';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Отмена';
    cancel.style.cssText = [
      'display:block',
      'width:100%',
      'margin-top:16px',
      'padding:10px',
      'border:0',
      'border-radius:8px',
      'background:#f0f0f0',
      'color:#333',
      'font-size:14px',
      'cursor:pointer',
    ].join(';');
    cancel.onclick = () => {
      if (this.pending) {
        const p = this.pending;
        this.pending = null;
        p.reject(new Error('Проверка капчи отменена'));
      } else {
        this.hideOverlay();
      }
    };

    card.appendChild(title);
    card.appendChild(this.captchaHost);
    card.appendChild(cancel);
    this.overlay.appendChild(card);
    document.body.appendChild(this.overlay);
  }

  private hideOverlay(): void {
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
  }

  private ensureWidget(sitekey: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.smartCaptcha) {
        reject(new Error('smartCaptcha API недоступен'));
        return;
      }

      if (!this.captchaHost) {
        reject(new Error('Контейнер капчи не создан'));
        return;
      }

      if (this.widgetId != null) {
        resolve();
        return;
      }

      this.widgetId = window.smartCaptcha.render(this.captchaHost, {
        sitekey,
        hl: 'ru',
        callback: (token: string) => {
          if (this.pending && typeof token === 'string' && token.length > 0) {
            const p = this.pending;
            this.pending = null;
            p.resolve(token);
          }
        },
      });

      resolve();
    });
  }
}
